/**
 * HistoryToolbar Component
 * Bottom toolbar for history view with selection management and copy actions
 */

import { useMemo } from 'react';
import { Copy, X } from 'lucide-react';
import { useHistoryStore } from '@/stores/history';
import { useClipboardStore } from '@/stores/clipboard';
import { toast } from '@/components/ui/Toast';

/**
 * Toolbar for managing track selection and copying to clipboard
 */
export function HistoryToolbar() {
  const { currentPage, selectedTracks, clearSelection } = useHistoryStore();
  const { setTracks } = useClipboardStore();

  const selectionCount = selectedTracks.size;
  const hasSelection = selectionCount > 0;

  // Get selected tracks from current page
  const selectedTrackObjects = useMemo(() => {
    if (selectionCount === 0) return [];

    return currentPage.items
      .filter((item) => selectedTracks.has(item.track.id))
      .map((item) => item.track);
  }, [currentPage.items, selectedTracks, selectionCount]);

  // Handle copy to clipboard
  const handleCopy = () => {
    if (selectionCount === 0) return;

    setTracks(selectedTrackObjects);
    toast.success(`Copied ${selectionCount} track${selectionCount > 1 ? 's' : ''} to clipboard`);
    clearSelection();
  };

  // Don't render toolbar when no selection
  if (!hasSelection) {
    return null;
  }

  return (
    <div className="border-t border-zinc-800 bg-zinc-900 p-4">
      <div className="flex items-center justify-between">
        {/* Selection Count */}
        <div className="text-sm text-zinc-300">
          <span className="font-medium text-emerald-400">{selectionCount}</span>
          {' '}track{selectionCount !== 1 ? 's' : ''} selected
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Clear Selection Button */}
          <button
            onClick={clearSelection}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
            title="Clear selection"
          >
            <X className="w-4 h-4" />
            <span className="text-sm">Clear</span>
          </button>

          {/* Copy to Clipboard Button */}
          <button
            onClick={handleCopy}
            disabled={!hasSelection}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            title="Copy selected tracks to clipboard"
          >
            <Copy className="w-4 h-4" />
            <span className="text-sm font-medium">
              Copy to Clipboard
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
